import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import MainLayouts from "../../layout/MainLayouts";
import AlumniCard from "./AlumniCard";
import { useContent } from "../../services/BlogContext";
import { alumniStories } from "../../data/alumniStories";
import StoryCard from "../../components/Testimonials/StoryCard";
import StoryLightbox from "../../components/Testimonials/StoryLightbox";

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
  </svg>
);

const FILTERS = [
  { key: "all", labelKey: "alumniPage.filterAll" },
  { key: "data", labelKey: "alumniPage.filterData" },
  { key: "ai", labelKey: "alumniPage.filterAi" },
];

function AlumniStory() {
  const { t } = useTranslation();
  const { alumni, fetchAlumni } = useContent();
  const [filter, setFilter] = useState("all");
  const [openStory, setOpenStory] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchAlumni();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibleStories = useMemo(
    () => (filter === "all" ? alumniStories : alumniStories.filter((s) => s.category === filter)),
    [filter]
  );
  const profiles = alumni?.data || [];

  return (
    <MainLayouts>
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#0b0f19] pt-36 md:pt-44 pb-20 md:pb-28">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0b0f19] via-[#1a2333] to-[#05070c]" />
        <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-primary/20 blur-[120px]" />
        <div className="relative z-10 max-w-[1560px] mx-auto px-6">
          <div className="text-primary text-[12px] font-bold tracking-[0.25em] uppercase mb-5">
            {t("alumniPage.kicker")}
          </div>
          <h1 className="text-white text-4xl md:text-6xl lg:text-[72px] font-bold leading-[1.05] tracking-tight mb-6">
            {t("alumniPage.title")}<span className="text-primary">.</span>
          </h1>
          <p className="text-white/70 text-lg max-w-[560px] leading-relaxed border-l border-primary/40 pl-6 mb-10">
            {t("alumniPage.subtitle")}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/courses"
              className="bg-primary hover:bg-[#b0451e] text-white transition-colors rounded-full pl-5 pr-2 py-2 inline-flex items-center gap-3 group self-start shadow-md w-full sm:w-auto justify-between sm:justify-start"
            >
              <span className="text-[11px] font-bold uppercase tracking-wider">{t("home.hero.exploreCourses")}</span>
              <div className="w-7 h-7 rounded-full bg-white flex items-center justify-center text-primary shrink-0">
                <ArrowIcon />
              </div>
            </Link>
            <Link
              to="/scholarships"
              className="bg-transparent hover:bg-white/10 text-white rounded-full px-5 py-3 inline-flex items-center justify-center text-[11px] font-bold border border-white/30 transition-colors uppercase tracking-wider w-full sm:w-auto self-start"
            >
              {t("home.hero.claimScholarship")}
            </Link>
          </div>
        </div>
      </section>

      {/* Story wall */}
      <section className="bg-gray-50 py-16 lg:py-24">
        <div className="max-w-[1560px] mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-10">
            <div className="flex flex-wrap gap-2" role="tablist" aria-label={t("alumniPage.title")}>
              {FILTERS.map(({ key, labelKey }) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={filter === key}
                  onClick={() => setFilter(key)}
                  className={`rounded-full px-5 py-2.5 text-[11px] font-bold uppercase tracking-wider transition-colors ${
                    filter === key
                      ? "bg-[#0b0f19] text-white"
                      : "bg-white text-gray-500 border border-gray-200 hover:border-primary/40 hover:text-primary"
                  }`}
                >
                  {t(labelKey)}
                </button>
              ))}
            </div>
            <p className="text-gray-400 text-sm">{t("alumniPage.showing", { count: visibleStories.length })}</p>
          </div>

          <div className="columns-1 sm:columns-2 lg:columns-3 gap-6">
            {visibleStories.map((story) => (
              <StoryCard key={story.id} story={story} onOpen={setOpenStory} variant="grid" />
            ))}
          </div>
        </div>
      </section>

      {/* Profiles managed from the admin (only rendered when there are any) */}
      {profiles.length > 0 && (
        <section className="bg-white py-16 lg:py-24">
          <div className="max-w-[1560px] mx-auto px-6">
            <div className="text-[11px] font-bold tracking-[0.15em] uppercase text-gray-400 mb-6 flex items-center gap-3">
              <span className="block w-8 h-[2px] bg-primary"></span>
              {t("alumniPage.moreTitle")}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {profiles.map((person) => (
                <AlumniCard key={person.id} alumni={person} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Closing CTA */}
      <section className="bg-[#0b0f19] py-20 lg:py-28">
        <div className="max-w-[1560px] mx-auto px-6 flex flex-col lg:flex-row lg:items-end justify-between gap-8">
          <div>
            <h2 className="text-white text-3xl md:text-5xl font-bold mb-4">
              {t("alumniPage.ctaTitle")}<span className="text-primary">.</span>
            </h2>
            <p className="text-white/60 text-lg max-w-[520px] leading-relaxed">{t("alumniPage.ctaText")}</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/scholarships"
              className="bg-primary hover:bg-[#b0451e] text-white transition-colors rounded-full pl-5 pr-2 py-2 inline-flex items-center gap-3 group shadow-md w-full sm:w-auto justify-between sm:justify-start"
            >
              <span className="text-[11px] font-bold uppercase tracking-wider">{t("home.hero.claimScholarship")}</span>
              <div className="w-7 h-7 rounded-full bg-white flex items-center justify-center text-primary shrink-0">
                <ArrowIcon />
              </div>
            </Link>
            <Link
              to="/courses"
              className="bg-transparent hover:bg-white/10 text-white rounded-full px-5 py-3 inline-flex items-center justify-center text-[11px] font-bold border border-white/30 transition-colors uppercase tracking-wider w-full sm:w-auto"
            >
              {t("home.hero.exploreCourses")}
            </Link>
          </div>
        </div>
      </section>

      {openStory && <StoryLightbox story={openStory} onClose={() => setOpenStory(null)} />}
    </MainLayouts>
  );
}

export default AlumniStory;
